import { Link } from "@tanstack/react-router";
import { ArrowUpRight, Clock } from "lucide-react";
import { m } from "@/paraglide/messages";
import { BlogCover } from "./BlogCover";

interface BlogPostSummary {
  id: string;
  publishedAt: string | null;
  coverImage: string | null;
  coverImageMobile?: string | null;
  readingTimeMinutes: number;
  slug: string;
  title: string;
  description: string;
  toolSlug: string;
  topicSlug: string;
  topicName: string;
}

export function BlogPostCard({ post }: { post: BlogPostSummary }) {
  const publishedDate = post.publishedAt
    ? new Date(post.publishedAt).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : null;

  return (
    <Link
      to="/blog/$tool/$topic/$slug"
      params={{
        tool: post.toolSlug,
        topic: post.topicSlug,
        slug: post.slug,
      }}
      className="group relative flex flex-col overflow-hidden rounded-xl border border-border bg-card shadow-[0_1px_2px_oklch(0.2_0.03_265/0.06),0_4px_16px_-4px_oklch(0.2_0.03_265/0.1)] transition-all duration-300 hover:-translate-y-1 hover:border-primary/60 hover:shadow-[0_2px_4px_oklch(0.2_0.03_265/0.08),0_12px_32px_-8px_oklch(0.2_0.03_265/0.16)] dark:border-white/[0.14] dark:bg-[oklch(0.255_0.022_265)] dark:shadow-[0_1px_0_oklch(1_0_0/0.1)_inset,0_24px_48px_-20px_rgba(0,0,0,0.95)] dark:ring-1 dark:ring-inset dark:ring-white/[0.04] dark:hover:shadow-[0_1px_0_oklch(1_0_0/0.14)_inset,0_28px_56px_-18px_oklch(0.5_0.22_260/0.45)]"
    >
      <BlogCover
        coverImage={post.coverImage}
        coverImageMobile={post.coverImageMobile}
        alt={post.title}
        size="card"
        className="aspect-[16/10] w-full"
      />

      <div className="relative flex flex-1 flex-col p-5">
        {/* Topic eyebrow — motorsport gold */}
        <span className="mb-2.5 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-secondary">
          {post.topicName}
        </span>

        <h2 className="mb-2 text-base font-bold leading-snug tracking-tight transition-colors group-hover:text-primary">
          {post.title}
        </h2>

        <p className="mb-5 line-clamp-2 flex-1 text-sm leading-relaxed text-muted-foreground">
          {post.description}
        </p>

        {/* Footer — timing-screen metadata */}
        <div className="flex items-center justify-between border-t border-border/30 pt-4">
          <div className="flex items-center gap-2.5 font-mono text-[11px] text-muted-foreground/90">
            {publishedDate && <time>{publishedDate}</time>}
            {publishedDate && (
              <span className="h-0.5 w-0.5 rounded-full bg-muted-foreground/40" />
            )}
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {post.readingTimeMinutes} {m.blog_minutes_read()}
            </span>
          </div>
          <ArrowUpRight className="h-4 w-4 text-muted-foreground transition-all group-hover:text-primary group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </div>
      </div>
    </Link>
  );
}
